import type { ReactElement } from "react";

import {
  type Testimonial,
  getGeneralTestimonials,
  getTestimonialsForSuite,
  getTestimonialsForProduct,
} from "../data/testimonials";

/**
 * Cycle 8 STORY-703: Testimonials section component.
 *
 * Reusable on:
 * - HomePage (general; first 5)
 * - SuiteLandingPage (suite-filtered)
 * - ProductDetailPage (product-filtered; optional)
 *
 * Note: testimonials are placeholder data — see `data/testimonials.ts`.
 */

interface Props {
  /** Filter by suite slug (e.g. "safety", "logistics") */
  filterSuite?: string;
  /** Filter by product slug */
  filterProduct?: string;
  /** Max number of testimonials to show */
  limit?: number;
  /** Optional title override */
  title?: string;
  /** Optional subtitle */
  subtitle?: string;
}

export function Testimonials({
  filterSuite,
  filterProduct,
  limit = 6,
  title = "Что говорят клиенты",
  subtitle = "Отзывы заказчиков, использующих платформу IT-Navigate в реальных проектах",
}: Props): ReactElement | null {
  let items: Testimonial[];

  if (filterProduct) {
    items = getTestimonialsForProduct(filterProduct);
  } else if (filterSuite) {
    items = getTestimonialsForSuite(filterSuite);
  } else {
    items = getGeneralTestimonials(limit);
  }

  items = items.slice(0, limit);

  if (items.length === 0) return null;

  return (
    <section className="testimonials" aria-labelledby="testimonials-title">
      <header className="testimonials__header">
        <h2 id="testimonials-title" className="testimonials__title">
          {title}
        </h2>
        <p className="testimonials__subtitle">{subtitle}</p>
      </header>

      <div className="testimonials__grid">
        {items.map((t) => (
          <article key={t.id} className="testimonial-card">
            <div className="testimonial-card__quote-mark" aria-hidden="true">
              &ldquo;
            </div>
            <blockquote className="testimonial-card__quote">{t.quote}</blockquote>
            <footer className="testimonial-card__footer">
              <div
                className="testimonial-card__avatar"
                style={{ backgroundColor: t.accentColor }}
                aria-hidden="true"
              >
                {t.initials}
              </div>
              <div className="testimonial-card__author">
                <div className="testimonial-card__name">{t.author}</div>
                <div className="testimonial-card__role">{t.role}</div>
                <div className="testimonial-card__company">{t.company}</div>
              </div>
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}
